import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Select,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { TextArea } = Input;

const Articulos = () => {
  const { hasPermission } = usePermissions();
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingArticulo, setEditingArticulo] = useState(null);
  const [form] = Form.useForm();
  const precioCompra = Form.useWatch('precio_compra', form);
  const porcMarcacion = Form.useWatch('porc_marcacion', form);
  const ivaPorcentaje = Form.useWatch('iva_porcentaje', form);
  const precioVenta = Form.useWatch('precio_venta', form);
  const manualPrecioVentaRef = useRef(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [categorias, setCategorias] = useState([]);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const formatMoneyAr = (value) => (
    `$${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  );

  // Cargar categorías
  const loadCategorias = async () => {
    try {
      const response = await apiClient.get('/gestion/categorias-articulos');
      if (response.ok) {
        const data = await response.json();
        // Filtrar solo las activas
        setCategorias(data.filter(cat => cat.cat_art_activo));
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  // Cargar artículos
  const loadArticulos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar los artículos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los artículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticulos();
    loadCategorias();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calcular precios derivados al cambiar precio compra, % marcación o IVA
  useEffect(() => {
    if (!modalVisible) return;
    if (manualPrecioVentaRef.current) {
      manualPrecioVentaRef.current = false;
      const compra = Number(precioCompra);
      const marcacion = porcMarcacion !== undefined && porcMarcacion !== null && porcMarcacion !== '' ? Number(porcMarcacion) : null;
      const iva = ivaPorcentaje !== undefined && ivaPorcentaje !== null && ivaPorcentaje !== '' ? Number(ivaPorcentaje) : null;
      const sinIva = (compra >= 0 && marcacion !== null)
        ? Math.round(compra * (1 + marcacion / 100) * 100) / 100
        : null;
      const pv = form.getFieldValue('precio_venta');
      const updates = {};
      if (sinIva != null) updates.precio_venta_sin_iva = sinIva;
      if (pv != null && pv !== '') {
        updates.precio_venta_con_iva = Number(pv);
      }
      if (Object.keys(updates).length) form.setFieldsValue(updates);
      return;
    }
    const compra = Number(precioCompra);
    const marcacion = porcMarcacion !== undefined && porcMarcacion !== null && porcMarcacion !== '' ? Number(porcMarcacion) : null;
    const iva = ivaPorcentaje !== undefined && ivaPorcentaje !== null && ivaPorcentaje !== '' ? Number(ivaPorcentaje) : null;
    const sinIva = (compra >= 0 && marcacion !== null)
      ? Math.round(compra * (1 + marcacion / 100) * 100) / 100
      : (compra >= 0 ? compra : null);
    const conIva = sinIva != null && iva !== null
      ? Math.round(sinIva * (1 + iva / 100) * 100) / 100
      : sinIva;
    const updates = {};
    if (sinIva != null) updates.precio_venta_sin_iva = sinIva;
    if (conIva != null) {
      updates.precio_venta_con_iva = conIva;
      updates.precio_venta = conIva;
    }
    if (Object.keys(updates).length) form.setFieldsValue(updates);
  }, [modalVisible, precioCompra, porcMarcacion, ivaPorcentaje]);

  // Recalcular % marcación cuando el usuario pone a mano el "Precio de venta (a cobrar)"
  useEffect(() => {
    if (!modalVisible) return;
    const pv = precioVenta != null && precioVenta !== '' ? Number(precioVenta) : null;
    const compra = Number(precioCompra);
    const iva = ivaPorcentaje !== undefined && ivaPorcentaje !== null && ivaPorcentaje !== '' ? Number(ivaPorcentaje) : null;
    if (pv == null || compra <= 0 || iva == null) return;
    const conIvaDesdeMarcacion = (() => {
      const marcacion = porcMarcacion !== undefined && porcMarcacion !== null && porcMarcacion !== '' ? Number(porcMarcacion) : null;
      if (marcacion == null) return null;
      const sinIva = Math.round(compra * (1 + marcacion / 100) * 100) / 100;
      return Math.round(sinIva * (1 + iva / 100) * 100) / 100;
    })();
    const dif = conIvaDesdeMarcacion != null ? Math.abs(pv - conIvaDesdeMarcacion) : 0.02;
    if (dif < 0.01) return;
    const precioVentaSinIva = pv / (1 + iva / 100);
    const nuevaMarcacion = Math.round((precioVentaSinIva / compra - 1) * 10000) / 100;
    manualPrecioVentaRef.current = true;
    form.setFieldsValue({
      porc_marcacion: nuevaMarcacion,
      precio_venta_sin_iva: Math.round(precioVentaSinIva * 100) / 100,
      precio_venta_con_iva: pv,
    });
  }, [modalVisible, precioVenta, precioCompra, ivaPorcentaje]);

  // Filtrar artículos
  const articulosFiltrados = articulos.filter(art => {
    const term = filtroNombre.toLowerCase();
    const matchNombre = !filtroNombre ||
      art.art_nombre?.toLowerCase().includes(term) ||
      art.art_codigo_barras?.toLowerCase().includes(term);
    const matchCategoria = !filtroCategoria || art.art_categoria === filtroCategoria;
    return matchNombre && matchCategoria;
  });

  const articulosPaginados = articulosFiltrados.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const cargarLogoCaleta = () => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = '/logo-caleta.png';
  });

  const handleImprimirPdf = async () => {
    if (!articulosFiltrados.length) {
      message.warning('No hay artículos para imprimir con el filtro actual');
      return;
    }

    try {
      const doc = new jsPDF();
      const logoDataUrl = await cargarLogoCaleta();
      let currentY = 16;

      if (logoDataUrl) {
        const logoWidth = 50;
        const logoHeight = 20;
        const x = (doc.internal.pageSize.getWidth() - logoWidth) / 2;
        doc.addImage(logoDataUrl, 'PNG', x, currentY, logoWidth, logoHeight);
        currentY += logoHeight + 8;
      }

      doc.setFontSize(14);
      doc.text('Listado de artículos', 14, currentY);
      currentY += 7;

      doc.setFontSize(10);
      const categoriaTexto = filtroCategoria ? `Categoría: ${filtroCategoria}` : 'Categoría: Todas';
      doc.text(categoriaTexto, 14, currentY);
      currentY += 3;

      autoTable(doc, {
        startY: currentY + 3,
        head: [['Nombre del artículo', 'Precio de venta']],
        body: articulosFiltrados.map((art) => [
          art.art_nombre || '-',
          formatMoneyAr(art.art_precio_venta),
        ]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [24, 144, 255] },
      });

      const fecha = new Date().toISOString().slice(0, 10);
      const categoriaNombre = filtroCategoria ? filtroCategoria.toLowerCase().replace(/\s+/g, '-') : 'todas';
      doc.save(`articulos-${categoriaNombre}-${fecha}.pdf`);
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error('No se pudo generar el PDF de artículos');
    }
  };

  // Abrir modal para crear/editar
  const handleOpenModal = (articulo = null) => {
    setEditingArticulo(articulo);
    if (articulo) {
      form.setFieldsValue({
        nombre: articulo.art_nombre,
        codigo_barras: articulo.art_codigo_barras || undefined,
        descripcion: articulo.art_descripcion,
        precio_compra: articulo.art_precio_compra,
        precio_venta: articulo.art_precio_venta,
        porc_marcacion: articulo.art_porc_marcacion ?? undefined,
        precio_venta_sin_iva: articulo.art_precio_venta_sin_iva ?? undefined,
        iva_porcentaje: articulo.art_iva_porcentaje ?? undefined,
        precio_venta_con_iva: articulo.art_precio_venta_con_iva ?? undefined,
        stock_actual: articulo.art_stock_actual,
        stock_minimo: articulo.art_stock_minimo,
        categoria_id: articulo.art_categoria_id || null,
        mueve_stock: articulo.art_mueve_stock,
        mueve_caja: true,
        activo: articulo.art_activo,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        precio_compra: 0,
        precio_venta: 0,
        porc_marcacion: undefined,
        precio_venta_sin_iva: undefined,
        iva_porcentaje: undefined,
        precio_venta_con_iva: undefined,
        stock_actual: 0,
        stock_minimo: 0,
        categoria_id: null,
        mueve_stock: true,
        mueve_caja: true,
        activo: true,
      });
    }
    setModalVisible(true);
  };

  // Guardar artículo
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const dataToSend = {
        ...values,
        codigo_barras: values.codigo_barras != null ? String(values.codigo_barras).trim() : null,
        precio_compra: values.precio_compra ?? 0,
        precio_venta: values.precio_venta ?? 0,
        stock_actual: values.stock_actual ?? 0,
        stock_minimo: values.stock_minimo ?? 0,
        porc_marcacion: values.porc_marcacion,
        precio_venta_sin_iva: values.precio_venta_sin_iva,
        iva_porcentaje: values.iva_porcentaje,
        precio_venta_con_iva: values.precio_venta_con_iva,
      };

      if (editingArticulo) {
        const response = await apiClient.put(
          `/gestion/articulos/${editingArticulo.art_id}`,
          dataToSend
        );
        if (response.ok) {
          message.success('Artículo actualizado correctamente');
          setModalVisible(false);
          loadArticulos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el artículo');
        }
      } else {
        const response = await apiClient.post('/gestion/articulos', dataToSend);
        if (response.ok) {
          message.success('Artículo creado correctamente');
          setModalVisible(false);
          loadArticulos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el artículo');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el artículo');
    }
  };

  // Eliminar artículo
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/articulos/${id}`);
      if (response.ok) {
        message.success('Artículo eliminado correctamente');
        loadArticulos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el artículo');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el artículo');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'art_nombre',
      key: 'art_nombre',
    },
    {
      title: 'Cód. barras',
      dataIndex: 'art_codigo_barras',
      key: 'art_codigo_barras',
      render: (cb) => cb || '-',
    },
    {
      title: 'Categoría',
      dataIndex: 'art_categoria',
      key: 'art_categoria',
      render: (categoria) => <Tag color="blue">{categoria}</Tag>,
    },
    {
      title: 'Precio Compra',
      dataIndex: 'art_precio_compra',
      key: 'art_precio_compra',
      render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
    },
    {
      title: 'Precio Venta',
      dataIndex: 'art_precio_venta',
      key: 'art_precio_venta',
      render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
    },
    {
      title: 'Stock',
      key: 'stock',
      render: (_, record) => {
        const stockBajo = record.art_stock_actual <= record.art_stock_minimo;
        return (
          <Space>
            <span>{record.art_stock_actual}</span>
            {stockBajo && record.art_mueve_stock && (
              <Tag color="red" icon={<WarningOutlined />}>
                Bajo
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Mueve Stock',
      dataIndex: 'art_mueve_stock',
      key: 'art_mueve_stock',
      render: (mueve) => (
        <Tag color={mueve ? 'green' : 'default'}>
          {mueve ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'art_activo',
      key: 'art_activo',
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    ...((hasPermission('articulos', 'editar') || hasPermission('articulos', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          {hasPermission('articulos', 'editar') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            >
              Editar
            </Button>
          )}
          {hasPermission('articulos', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar este artículo?"
              onConfirm={() => handleDelete(record.art_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Artículos
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadArticulos}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('articulos', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal(null)}
              >
                Nuevo Artículo
              </Button>
            )}
            <Button onClick={handleImprimirPdf}>
              Imprimir PDF
            </Button>
          </Space>

          {/* Filtros */}
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Buscar por nombre
              </label>
              <Input
                placeholder="Buscar artículo..."
                value={filtroNombre}
                onChange={(e) => {
                  setFiltroNombre(e.target.value);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                allowClear
              />
            </div>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Filtrar por categoría
              </label>
              <Select
                placeholder="Todas las categorías"
                value={filtroCategoria}
                onChange={(value) => {
                  setFiltroCategoria(value);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                allowClear
                style={{ width: '100%' }}
                showSearch
              >
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_art_id} value={cat.cat_art_nombre}>
                    {cat.cat_art_nombre}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {(filtroNombre || filtroCategoria) && (
              <Button
                onClick={() => {
                  setFiltroNombre('');
                  setFiltroCategoria(null);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
              >
                Limpiar
              </Button>
            )}
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {articulosPaginados.map(articulo => (
              <Card
                key={articulo.art_id}
                hoverable
                style={{ borderLeft: '4px solid #1890ff', borderRadius: '8px' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                      {articulo.art_nombre}
                    </div>
                    {articulo.art_codigo_barras && (
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Cód. barras: {articulo.art_codigo_barras}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Categoría: <Tag color="blue">{articulo.art_categoria}</Tag>
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Precio Venta: ${parseFloat(articulo.art_precio_venta).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Stock: {articulo.art_stock_actual}
                      {articulo.art_stock_actual <= articulo.art_stock_minimo && articulo.art_mueve_stock && (
                        <Tag color="red" style={{ marginLeft: 8 }}>Bajo</Tag>
                      )}
                    </div>
                  </div>
                  {(hasPermission('articulos', 'editar') || hasPermission('articulos', 'eliminar')) && (
                    <Space wrap>
                      {hasPermission('articulos', 'editar') && (
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleOpenModal(articulo)}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission('articulos', 'eliminar') && (
                        <Popconfirm
                          title="¿Estás seguro de eliminar este artículo?"
                          onConfirm={() => handleDelete(articulo.art_id)}
                          okText="Sí"
                          cancelText="No"
                        >
                          <Button type="link" danger icon={<DeleteOutlined />}>
                            Eliminar
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
            {articulosFiltrados.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={articulosFiltrados.length}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                  }}
                  onShowSizeChange={(current, size) => {
                    setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} artículos`}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={articulosPaginados}
              loading={loading}
              rowKey="art_id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: articulosFiltrados.length,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} artículos`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                },
              }}
            />
          </>
        )}

        {/* Modal para crear/editar artículo */}
        <Modal
          title={editingArticulo ? 'Editar Artículo' : 'Nuevo Artículo'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input placeholder="Nombre del artículo" />
            </Form.Item>

            <Form.Item
              name="codigo_barras"
              label="Código de barras"
            >
              <Input placeholder="Ej: 7790310987654" maxLength={50} allowClear />
            </Form.Item>

            <Form.Item name="descripcion" label="Descripción">
              <TextArea rows={3} placeholder="Descripción del artículo" />
            </Form.Item>

            <Form.Item
              name="precio_compra"
              label="Precio de compra"
              rules={[{ required: true, message: 'El precio de compra es requerido' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="0.00"
              />
            </Form.Item>

            <Form.Item name="porc_marcacion" label="% marcación">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                addonAfter="%"
                placeholder="Ej. 50"
              />
            </Form.Item>

            <Form.Item name="precio_venta_sin_iva" label="Precio venta sin IVA">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Calculado"
                readOnly
              />
            </Form.Item>

            <Form.Item name="iva_porcentaje" label="IVA del artículo">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={0.5}
                addonAfter="%"
                placeholder="Ej. 21"
              />
            </Form.Item>

            <Form.Item name="precio_venta_con_iva" label="Precio de venta con IVA">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Calculado"
                readOnly
              />
            </Form.Item>

            <Form.Item
              name="precio_venta"
              label="Precio de venta (a cobrar)"
              rules={[{ required: true, message: 'El precio de venta es requerido' }]}
              help="Se completa con el precio con IVA. Si lo ingresás a mano, se recalcula el % de marcación."
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              name="stock_actual"
              label="Stock Actual"
              rules={[{ required: true, message: 'El stock actual es requerido' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              name="stock_minimo"
              label="Stock Mínimo"
              rules={[{ required: true, message: 'El stock mínimo es requerido' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              name="categoria_id"
              label="Categoría"
              rules={[{ required: true, message: 'La categoría es requerida' }]}
            >
              <Select placeholder="Seleccione la categoría" showSearch>
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_art_id} value={cat.cat_art_id}>
                    {cat.cat_art_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="mueve_stock"
              label="Mueve Stock"
              valuePropName="checked"
            >
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Articulos;
